import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { pedirJson } from "./api";

/* Migração do Movidesk: o que a tela interna mostra (somente leitura) do importador.
   O contrato é o de `api/app/dominios/migracao/router.py`. Rodar o importador é pela ferramenta
   `python -m app.ferramentas.importar_movidesk`; aqui só se lê o resultado. */

const uuid = z.string().uuid();

const esquemaContagem = z.object({
  lidos: z.number(),
  criados: z.number(),
  reaproveitados: z.number(),
  atualizados: z.number(),
  inalterados: z.number(),
  ignorados: z.number(),
  erros: z.number(),
});

const esquemaDivergencia = z.object({
  codigo: z.string(),
  tipo: z.string(),
  id_externo: z.string(),
  detalhe: z.record(z.string(), z.unknown()),
});

const esquemaResumo = z.object({
  contagens: z.record(z.string(), esquemaContagem),
  divergencias: z.array(esquemaDivergencia),
  divergencias_por_codigo: z.record(z.string(), z.number()),
  divergencias_omitidas: z.number(),
  anexos_pendentes: z.number(),
  reconciliacao: z.object({
    confere: z.boolean(),
    contagens_fecham: z.boolean(),
    acoes_esperadas: z.number(),
    acoes_conferidas: z.number(),
    orfaos: z.number(),
  }),
});

const esquemaExecucao = z.object({
  id: uuid,
  origem: z.enum(["api", "arquivos"]),
  simulacao: z.boolean(),
  estado: z.enum(["em_andamento", "concluida", "falhou"]),
  escopo: z.array(z.string()),
  desde: z.string().nullable(),
  iniciada_em: z.string(),
  concluida_em: z.string().nullable(),
  confere: z.boolean().nullable(),
  chamados_criados: z.number(),
  pessoas_criadas: z.number(),
  organizacoes_criadas: z.number(),
});

const esquemaExecucaoDetalhada = esquemaExecucao.extend({
  erro: z.string().nullable(),
  // Execução que ainda está em andamento (ou falhou cedo) não tem relatório.
  resumo: z.union([esquemaResumo, z.object({}).strict()]),
});

export type ExecucaoDeMigracao = z.infer<typeof esquemaExecucao>;
export type ExecucaoDetalhada = z.infer<typeof esquemaExecucaoDetalhada>;
export type ResumoDaMigracao = z.infer<typeof esquemaResumo>;
export type Divergencia = z.infer<typeof esquemaDivergencia>;

const CHAVE = ["migracao"] as const;

export function useExecucoesDaMigracao(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "execucoes"],
    queryFn: ({ signal }) => pedirJson("/migracao/execucoes", z.array(esquemaExecucao), { signal }),
    enabled,
  });
}

export function useExecucaoDaMigracao(id: string | null) {
  return useQuery({
    queryKey: [...CHAVE, "execucoes", id],
    queryFn: ({ signal }) =>
      pedirJson(`/migracao/execucoes/${encodeURIComponent(id ?? "")}`, esquemaExecucaoDetalhada, {
        signal,
      }),
    enabled: id !== null,
  });
}

export const ROTULO_DO_ESTADO_DA_EXECUCAO: Record<ExecucaoDeMigracao["estado"], string> = {
  em_andamento: "Em andamento",
  concluida: "Concluída",
  falhou: "Falhou",
};

export const ROTULO_DA_ORIGEM: Record<ExecucaoDeMigracao["origem"], string> = {
  api: "API do Movidesk",
  arquivos: "Arquivos exportados",
};

/** Tipos na ordem em que a tela os mostra, com o nome que a pessoa reconhece. */
export const TIPOS_DA_MIGRACAO: readonly { chave: string; rotulo: string }[] = [
  { chave: "organizacao", rotulo: "Organizações" },
  { chave: "pessoa", rotulo: "Pessoas" },
  { chave: "chamado", rotulo: "Chamados" },
  { chave: "acao", rotulo: "Ações (respostas e notas)" },
  { chave: "anexo", rotulo: "Anexos" },
];

/** Cada código de divergência do importador, dito de um jeito que quem confere entende. */
export const FRASE_DA_DIVERGENCIA: Record<string, string> = {
  organizacao_sem_cnpj_valido: "Organização sem CNPJ válido: não foi criada",
  organizacao_sem_nome: "Organização sem nome: não foi criada",
  organizacao_sem_id: "Organização sem identificador no Movidesk",
  pessoa_sem_email: "Pessoa sem e-mail: não foi criada",
  pessoa_sem_organizacao: "Pessoa sem organização conhecida: não foi criada",
  pessoa_sem_id: "Pessoa sem identificador no Movidesk",
  pessoa_em_varias_organizacoes: "Pessoa ligada a mais de uma organização: usada a primeira",
  pessoa_fora_do_escopo: "Departamento ou perfil que não é de cliente: não importado",
  agente_nao_importado: "Agente do Movidesk: não vira usuário (a equipe já tem conta)",
  email_de_outra_organizacao: "E-mail já usado por outra organização: não foi tomado",
  email_de_usuario_interno: "E-mail de um usuário interno: não foi tomado",
  chamado_sem_organizacao: "Chamado sem organização conhecida: não foi criado",
  chamado_sem_id: "Chamado sem identificador no Movidesk",
  chamado_excluido_na_fonte: "Chamado excluído no Movidesk: não importado",
  estado_desconhecido: "Estado que o portal não conhece: entrou como novo",
  urgencia_desconhecida: "Urgência que o portal não conhece: entrou como média",
  agente_sem_usuario: "Responsável sem conta no portal: chamado ficou sem responsável",
  equipe_inexistente: "Equipe que não existe no portal: chamado ficou sem equipe",
  acao_sem_id: "Ação sem identificador no Movidesk",
  acao_excluida_na_fonte: "Ação excluída no Movidesk: não importada",
  acao_sem_texto: "Ação sem texto: entrou com um texto de aviso",
  tipo_de_acao_desconhecido: "Tipo de ação desconhecido: tratada como nota interna",
  erro_ao_importar: "Falha ao gravar o registro: nada dele foi gravado",
};

export function fraseDaDivergencia(codigo: string): string {
  return FRASE_DA_DIVERGENCIA[codigo] ?? codigo;
}

/** Resumo de uma frase do resultado, para a lista de execuções. */
export function resumoDaExecucao(execucao: ExecucaoDeMigracao): string {
  if (execucao.estado === "em_andamento") return "Ainda em andamento";
  if (execucao.estado === "falhou") return "A execução falhou; nada novo foi confirmado";
  const partes = [
    `${execucao.organizacoes_criadas} organizações`,
    `${execucao.pessoas_criadas} pessoas`,
    `${execucao.chamados_criados} chamados`,
  ];
  return `${execucao.simulacao ? "Criaria" : "Criou"} ${partes.join(", ")}`;
}

export function quandoLegivel(valor: string | null): string {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? valor
    : data.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

export function temRelatorio(resumo: ExecucaoDetalhada["resumo"]): resumo is ResumoDaMigracao {
  return "contagens" in resumo;
}
